import AppUpdateDetector from "@components/utils/AppUpdateDetector";
import MemberSettingsLoader from "@components/utils/MemberSettingsLoader";
import ThemeColorLoader from "@components/utils/ThemeColorLoader";
import { AppStatsigProvider } from "@configs/statsig/StatsigProvider";
import { ThemePreset } from "@configs/theme-preset";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { GoogleOAuthProvider } from "@react-oauth/google";
import * as Sentry from "@sentry/react";
import { store } from "@stores";
import "@styles/App.css";
import { initializeFirebase, isFirebaseAvailable } from "@utils/firebase";
import { ThemeProvider } from "kz-ui-sdk";
import "kz-ui-sdk/dist/sdk-themes.css";
import { PropsWithChildren, useEffect } from "react";
import "react-aspect-ratio/aspect-ratio.css";
import { Provider } from "react-redux";
import AppContent from "./AppContent";
import ErrorFallback from "./ErrorFallback";
import "./configs/i18n";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

function App() {
  return (
    <ThemeProvider
      value={{
        preset: ThemePreset,
      }}
    >
      <Provider store={store}>
        <AppStatsigProvider>
          <ThemeColorLoader />
          <MemberSettingsLoader>
            <FirebaseInitializer />
            <WithGoogleOAuthProvider>
              <AppContent />
            </WithGoogleOAuthProvider>
          </MemberSettingsLoader>
        </AppStatsigProvider>
        <AppUpdateDetector />
      </Provider>
    </ThemeProvider>
  );
}

/**
 * Initialize Firebase if available
 */
const FirebaseInitializer = () => {
  const { googleAuthEnabled } = useMemberSettings();

  useEffect(() => {
    if (googleAuthEnabled && isFirebaseAvailable()) {
      initializeFirebase();
    }
  }, [googleAuthEnabled]);

  return null;
};

/**
 * Conditionally wrap children in GoogleOAuthProvider if Google OAuth is enabled
 * (but not using Firebase Auth)
 */
const WithGoogleOAuthProvider = ({ children }: PropsWithChildren) => {
  const { googleAuthEnabled } = useMemberSettings();
  const useFirebase = googleAuthEnabled && isFirebaseAvailable();

  // Only use GoogleOAuthProvider if Google Auth is enabled but Firebase is not available
  if (googleAuthEnabled && !useFirebase && GOOGLE_CLIENT_ID) {
    return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{children}</GoogleOAuthProvider>;
  }
  return children;
};

export const EnhancedApp = Sentry.withErrorBoundary(App, {
  fallback: ({ error, resetError }: { error: unknown; resetError: () => void }) => (
    <ErrorFallback
      error={error}
      resetError={resetError}
    />
  ),
});

export default App;
